import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { useBdReview, useRequestBdReview } from '../../../hooks/useBasicDesign.js';
import { ApiError } from '../../../lib/api/_request.js';
import { formatLocalYmd } from '../../../lib/date-utils.js';
import { BdApiErrorText } from './BdApiErrorText.js';
import { isDesignatedReviewer, reviewerCanSubmit, reviewStatusTone } from './bd-review.js';
import { BdFindingsList } from './BdFindingsList.js';
import { BdReviewChecklist } from './BdReviewChecklist.js';
import { BdReviewerPicker } from './BdReviewerPicker.js';
import type { BdArtifact, BdReview } from '../../../lib/api/basic-design.js';

export interface BdReviewPanelProps {
  /** 基本設計のチケット */
  bdKey: string;
  /** すべての節を保存し終えたか（依頼はそれからでないと通らない） */
  progressDone: boolean;
  /** 節を開く（観点の見る節から） */
  onOpenSection: (artifact: BdArtifact) => void;
}

/**
 * 基本設計のレビュー。状態・レビュアー・回、依頼後に中身が変わったときの案内と依頼し直し、
 * レビュアーを選んで依頼するフォーム、指摘の一覧（作者が直した・見送る・取り消す）、指名された
 * レビュアーにだけ出すチェックリスト（観点ごとの答え、承認・差し戻し）。
 *
 * 1 件の基本設計につきレビューは 1 つで、レビュアーは作者が依頼のときに指名する。誰が何を
 * 出せるかは API が判定し、指名されたレビュアーかどうかも API がメールで照らして
 * `callerIsReviewer` で返す（画面にはレビュアーのメールが来ない）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計のレビューの状態・依頼・指摘の解決・指名されたレビュアーのチェックリストと承認/差し戻しを 1 枚で扱う
 * @graph-connects useBdReview [calls] レビューの取得
 * @graph-connects useRequestBdReview [calls] レビューの依頼
 * @graph-connects BdFindingsList [calls] 指摘の一覧と解決
 * @graph-connects BdReviewChecklist [calls] レビュアーのチェックリスト
 * @graph-connects BdReviewerPicker [calls] レビュアーの選択
 */
export function BdReviewPanel({ bdKey, progressDone, onOpenSection }: BdReviewPanelProps) {
  const { t } = useTranslation();
  const query = useBdReview(bdKey);
  const review = query.data;

  return (
    <section aria-label={t('bd.review.title')}>
      <Card padding="sm">
        <CardContent className="mt-0 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
              {t('bd.review.title')}
            </h2>
            {review === undefined || review.status === null ? (
              <Badge size="sm">{t('bd.review.notRequested')}</Badge>
            ) : (
              <>
                <Badge size="sm" variant={reviewStatusTone(review.status)}>
                  {t(`bd.review.status.${review.status}`)}
                </Badge>
                {review.stale ? (
                  <Badge size="sm" variant="warning">
                    {t('bd.review.staleBadge')}
                  </Badge>
                ) : null}
                <span className="text-xs text-[var(--color-text-secondary)]">
                  {t('bd.review.meta', {
                    reviewer: review.reviewer?.nickName ?? '—',
                    round: review.round,
                  })}
                </span>
                <ApprovedNote review={review} />
              </>
            )}
          </div>

          {query.error !== null &&
          !(query.error instanceof ApiError && query.error.status === 404) ? (
            <BdApiErrorText error={query.error} />
          ) : query.isPending ? (
            <p className="text-sm text-[var(--color-text-muted)]">{t('bd.loading')}</p>
          ) : (
            <ReviewBody
              bdKey={bdKey}
              review={review ?? null}
              progressDone={progressDone}
              onOpenSection={onOpenSection}
            />
          )}
        </CardContent>
      </Card>
    </section>
  );
}

/** 承認の記録（承認済みで古くないときだけ）。 @graph-connects none */
function ApprovedNote({ review }: { review: BdReview }) {
  const { t } = useTranslation();
  if (review.status !== 'approved' || review.stale) return null;
  const approval = review.approvals
    .filter((a) => a.role === 'reviewer' && a.round === review.round)
    .pop();
  if (approval === undefined) return null;
  return (
    <span className="text-xs text-[var(--color-success-text)]">
      {t('bd.review.approvedAt', { date: formatLocalYmd(approval.at), actor: approval.actor })}
    </span>
  );
}

interface ReviewBodyProps {
  bdKey: string;
  review: BdReview | null;
  progressDone: boolean;
  onOpenSection: (artifact: BdArtifact) => void;
}

/**
 * レビューの中身。依頼していなければ依頼のフォームだけを出す。
 * @graph-connects RequestForm [calls] 依頼のフォーム
 */
function ReviewBody({ bdKey, review, progressDone, onOpenSection }: ReviewBodyProps) {
  const { t } = useTranslation();
  const requested = review !== null && review.status !== null;
  const designated = isDesignatedReviewer(review ?? undefined);

  return (
    <div className="space-y-4">
      {requested && review.stale ? (
        <div className="rounded-lg border border-[var(--color-warning)]/30 bg-[var(--color-warning-muted)] px-3 py-2 text-xs text-[var(--color-warning-text)]">
          {t('bd.review.staleBanner')}
        </div>
      ) : null}

      <RequestForm
        bdKey={bdKey}
        current={review?.reviewer?.nickName ?? ''}
        requested={requested}
        stale={review?.stale ?? false}
        progressDone={progressDone}
      />

      {review === null || !requested ? null : (
        <>
          <BdFindingsList bdKey={bdKey} review={review} />
          {designated ? (
            reviewerCanSubmit(review.status) && !review.stale ? (
              <BdReviewChecklist bdKey={bdKey} review={review} onOpenSection={onOpenSection} />
            ) : (
              <p className="text-xs text-[var(--color-text-muted)]">
                {review.stale ? t('bd.review.reviewerStale') : t('bd.review.reviewerWaiting')}
              </p>
            )
          ) : (
            <ChecklistReadOnly review={review} />
          )}
        </>
      )}
    </div>
  );
}

interface RequestFormProps {
  bdKey: string;
  /** 今のレビュアー（依頼し直しの初期値） */
  current: string;
  requested: boolean;
  stale: boolean;
  progressDone: boolean;
}

/**
 * レビュアーを選んで依頼する（依頼済みなら依頼し直す）。すべての節を保存するまでは押せない
 * （API も断る）。
 * @graph-connects useRequestBdReview [calls] レビューの依頼
 * @graph-connects BdReviewerPicker [calls] レビュアーの選択
 */
function RequestForm({ bdKey, current, requested, stale, progressDone }: RequestFormProps) {
  const { t } = useTranslation();
  const request = useRequestBdReview(bdKey);
  const [reviewer, setReviewer] = useState(current);
  const chosen = reviewer.trim();

  return (
    <form
      aria-label={t('bd.review.requestAria')}
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (chosen === '') return;
        request.mutate(chosen);
      }}
    >
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-56 flex-1">
          <BdReviewerPicker value={reviewer} onChange={setReviewer} />
        </div>
        <Button
          type="submit"
          size="sm"
          variant={stale ? 'primary' : 'secondary'}
          loading={request.isPending}
          disabled={!progressDone || chosen === '' || request.isPending}
        >
          {requested ? t('bd.review.requestAgain') : t('bd.review.request')}
        </Button>
      </div>
      {progressDone ? null : (
        <p className="text-xs text-[var(--color-text-muted)]">{t('bd.review.notDone')}</p>
      )}
      {request.isSuccess ? (
        <p role="status" className="text-xs text-[var(--color-success-text)]">
          {t('bd.review.requested')}
        </p>
      ) : null}
      {request.error === null ? null : <BdApiErrorText error={request.error} />}
    </form>
  );
}

/**
 * チェックリストの答え（指名されたレビュアー以外が読むもの）。まだ答えが無ければ出さない。
 * @graph-connects none
 */
function ChecklistReadOnly({ review }: { review: BdReview }) {
  const { t } = useTranslation();
  if (review.checklist.length === 0) return null;
  const labelOf = (key: string) => review.perspectives.find((p) => p.key === key)?.label ?? key;
  return (
    <div>
      <h3 className="text-xs font-semibold text-[var(--color-text-primary)]">
        {t('bd.review.checklist')}
      </h3>
      <ul aria-label={t('bd.review.checklist')} className="mt-1 space-y-1 text-xs">
        {review.checklist.map((entry) => (
          <li key={entry.perspective} className="flex flex-wrap gap-2">
            <Badge
              size="sm"
              variant={
                entry.result === 'ng' ? 'error' : entry.result === 'ok' ? 'success' : 'default'
              }
            >
              {t(`bd.review.result.${entry.result}`)}
            </Badge>
            <span className="font-medium text-[var(--color-text-primary)]">
              {labelOf(entry.perspective)}
            </span>
            <span className="text-[var(--color-text-secondary)]">{entry.evidence}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
